import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import type { Faro } from '@grafana/faro-web-sdk';
import { catchError, throwError } from 'rxjs';

type FaroSdk = typeof import('@grafana/faro-web-sdk');

let faro: Faro | undefined;

/**
 * Sends uncaught errors, console.error calls (which is how Angular reports
 * errors) and CSP violations to a Grafana Faro collector, if one is
 * configured. Nothing else is tracked and nothing is stored in the browser.
 * The SDK is only downloaded when a collector is configured.
 */
export async function startErrorReporting(
  app: string,
  url?: string,
  loadSdk: () => Promise<FaroSdk> = () => import('@grafana/faro-web-sdk'),
) {
  if (!url) return;
  try {
    const sdk = await loadSdk();
    faro = sdk.initializeFaro({
      url,
      app: { name: app },
      instrumentations: [
        new sdk.ErrorsInstrumentation(),
        new sdk.ConsoleInstrumentation(),
        new sdk.CSPInstrumentation(),
      ],
      consoleInstrumentation: {
        disabledLevels: [
          sdk.LogLevel.TRACE,
          sdk.LogLevel.DEBUG,
          sdk.LogLevel.LOG,
          sdk.LogLevel.INFO,
          sdk.LogLevel.WARN,
        ],
      },
      sessionTracking: { enabled: false },
    });
  } catch (error) {
    console.warn('Error reporting is unavailable', error);
  }
}

/**
 * Logs uncaught errors like Angular's default handler, but with the error as
 * the first argument, which is what Faro needs to keep its stack trace.
 */
export class ConsoleErrorHandler implements ErrorHandler {
  handleError(error: unknown) {
    console.error(error);
  }
}

/**
 * Reports API calls that failed on the server or got no response at all,
 * with the X-Request-Id under which the registrar logged the request.
 */
export const reportFailedRequests: HttpInterceptorFn = (req, next) =>
  next(req).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        (error.status === 0 || error.status >= 500)
      ) {
        faro?.api.pushError(
          new Error(`${req.method} ${req.url} failed with ${error.status}`),
          { context: { requestId: error.headers.get('X-Request-Id') ?? '' } },
        );
      }
      return throwError(() => error);
    }),
  );
